import { isAxiosError } from "axios";

export type ApiErrorDomain = "DECLINED_DEBTOR" | "DUPLICATE_LOAD_NUMBER";

export interface NormalizedApiError {
  message: string;
  messages: string[];
  domain?: ApiErrorDomain;
  raw: unknown;
}

export interface ExtractMessagesOptions {
  transformMessage?: (message: string, fieldName?: string) => string;
  fieldLabels?: Record<string, string>;
}

export interface NormalizeApiErrorOptions extends ExtractMessagesOptions {
  fallback?: string;
}

export const DEFAULT_API_ERROR_MESSAGE = "Something went wrong. Please try again.";

const MAX_DISPLAYABLE_MESSAGE_LENGTH = 500;

export const DECLINED_DEBTOR_MESSAGE =
  "You cannot factor a funding request for a debtor in declined status. Please go back and update this FR to a Non Factored Invoice Type to continue";

const DOMAIN_CODES: Record<string, ApiErrorDomain> = {
  DECLINED_DEBTOR: "DECLINED_DEBTOR",
  DEBTOR_DECLINED: "DECLINED_DEBTOR",
  DUPLICATE_LOAD_NUMBER: "DUPLICATE_LOAD_NUMBER"
};

const DECLINED_DEBTOR_PATTERN = /Debtor has been declined/i;

const DOMAIN_MESSAGE_PATTERNS: Array<[ApiErrorDomain, RegExp]> = [
  ["DECLINED_DEBTOR", DECLINED_DEBTOR_PATTERN],
  ["DUPLICATE_LOAD_NUMBER", /You have already submitted this load number/i],
  [
    "DUPLICATE_LOAD_NUMBER",
    /load number entered on this funding request is used by another funding request/i
  ],
  ["DUPLICATE_LOAD_NUMBER", /used by another funding request from the same debtor/i]
];

const DEFAULT_FIELD_LABELS: Record<string, string> = {
  user_load_number: "Load number",
  load_number: "Load number",
  debtor: "Customer",
  invoice_number: "Invoice number",
  amount: "Amount"
};

const GENERIC_ERROR_KEYS = new Set(["detail", "message", "error", "errors", "non_field_errors"]);

// Machine-readable envelope keys used for domain classification. They are never
// user-facing copy, so they must not surface as a `Code: DUPLICATE_LOAD_NUMBER` line.
const NON_MESSAGE_KEYS = new Set(["code", "error_code"]);

type ErrorDetailLike = {
  string?: unknown;
  code?: unknown;
};

export const stripHtml = (value: string) =>
  value
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();

export const humanizeFieldName = (field: string, fieldLabels?: Record<string, string>) =>
  fieldLabels?.[field] ??
  DEFAULT_FIELD_LABELS[field] ??
  field.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());

export const unwrapErrorsWrapper = (value: string) => {
  const wrappedErrorsMatch = value.match(/Errors:\s*(\{[\s\S]*\})/);
  return wrappedErrorsMatch?.[1] ?? value;
};

export const parseErrorDetailString = (value: string) => {
  const normalizedValue = unwrapErrorsWrapper(value);
  const directMatch = normalizedValue.match(/ErrorDetail\(string=(["'])([\s\S]*?)\1,\s*code=/);
  const parsedValue = directMatch?.[2] ?? normalizedValue;

  return stripHtml(parsedValue).replace(/\s+\n/g, "\n").replace(/\n\s+/g, "\n");
};

export const stripMatchingQuotes = (value: string) => {
  const trimmedValue = value.trim();
  const firstCharacter = trimmedValue[0];
  const lastCharacter = trimmedValue[trimmedValue.length - 1];

  if (
    trimmedValue.length >= 2 &&
    (firstCharacter === "'" || firstCharacter === '"') &&
    firstCharacter === lastCharacter
  ) {
    return trimmedValue.slice(1, -1);
  }

  return trimmedValue;
};

export const splitTopLevelValues = (value: string) => {
  const parts: string[] = [];
  let currentValue = "";
  let activeQuote: "'" | '"' | null = null;
  let isEscaped = false;
  let bracketDepth = 0;
  let braceDepth = 0;
  let parenthesisDepth = 0;

  for (const character of value) {
    if (activeQuote) {
      currentValue += character;

      if (isEscaped) {
        isEscaped = false;
      } else if (character === "\\") {
        isEscaped = true;
      } else if (character === activeQuote) {
        activeQuote = null;
      }

      continue;
    }

    if (character === "'" || character === '"') {
      activeQuote = character;
      currentValue += character;
      continue;
    }

    if (character === "[") {
      bracketDepth += 1;
    } else if (character === "]") {
      bracketDepth = Math.max(0, bracketDepth - 1);
    } else if (character === "{") {
      braceDepth += 1;
    } else if (character === "}") {
      braceDepth = Math.max(0, braceDepth - 1);
    } else if (character === "(") {
      parenthesisDepth += 1;
    } else if (character === ")") {
      parenthesisDepth = Math.max(0, parenthesisDepth - 1);
    }

    if (character === "," && bracketDepth === 0 && braceDepth === 0 && parenthesisDepth === 0) {
      if (currentValue.trim()) {
        parts.push(currentValue.trim());
      }
      currentValue = "";
      continue;
    }

    currentValue += character;
  }

  if (currentValue.trim()) {
    parts.push(currentValue.trim());
  }

  return parts;
};

export const findTopLevelDelimiterIndex = (value: string, delimiter: string) => {
  let activeQuote: "'" | '"' | null = null;
  let isEscaped = false;
  let bracketDepth = 0;
  let braceDepth = 0;
  let parenthesisDepth = 0;

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];

    if (activeQuote) {
      if (isEscaped) {
        isEscaped = false;
      } else if (character === "\\") {
        isEscaped = true;
      } else if (character === activeQuote) {
        activeQuote = null;
      }

      continue;
    }

    if (character === "'" || character === '"') {
      activeQuote = character;
      continue;
    }

    if (character === "[") {
      bracketDepth += 1;
      continue;
    }

    if (character === "]") {
      bracketDepth = Math.max(0, bracketDepth - 1);
      continue;
    }

    if (character === "{") {
      braceDepth += 1;
      continue;
    }

    if (character === "}") {
      braceDepth = Math.max(0, braceDepth - 1);
      continue;
    }

    if (character === "(") {
      parenthesisDepth += 1;
      continue;
    }

    if (character === ")") {
      parenthesisDepth = Math.max(0, parenthesisDepth - 1);
      continue;
    }

    if (
      character === delimiter &&
      bracketDepth === 0 &&
      braceDepth === 0 &&
      parenthesisDepth === 0
    ) {
      return index;
    }
  }

  return -1;
};

const isErrorDetailPayload = (value: unknown): value is ErrorDetailLike =>
  typeof value === "object" &&
  value !== null &&
  ("string" in value || "code" in value) &&
  Object.keys(value as Record<string, unknown>).every((key) => ["string", "code"].includes(key));

const applyTransform = (
  message: string,
  fieldName: string | undefined,
  options?: ExtractMessagesOptions
) => (options?.transformMessage ? options.transformMessage(message, fieldName) : message);

const formatFieldMessage = (
  message: string,
  fieldName: string | undefined,
  options?: ExtractMessagesOptions
) => {
  if (!message) {
    return [];
  }

  if (!fieldName) {
    return [message];
  }

  return [`${humanizeFieldName(fieldName, options?.fieldLabels)}: ${message}`];
};

const resolveNestedFieldName = (fieldName: string | undefined, key: string) =>
  GENERIC_ERROR_KEYS.has(key) ? fieldName : key;

export const extractMessagesFromStringPayload = (
  value: string,
  fieldName?: string,
  options?: ExtractMessagesOptions
): string[] => {
  const normalizedValue = unwrapErrorsWrapper(value).trim();

  if (!normalizedValue) {
    return [];
  }

  if (normalizedValue.startsWith("{") && normalizedValue.endsWith("}")) {
    const dictionaryBody = normalizedValue.slice(1, -1).trim();

    if (!dictionaryBody) {
      return [];
    }

    return splitTopLevelValues(dictionaryBody).flatMap((entry) => {
      const delimiterIndex = findTopLevelDelimiterIndex(entry, ":");

      if (delimiterIndex < 0) {
        return extractMessagesFromStringPayload(entry, fieldName, options);
      }

      const rawKey = entry.slice(0, delimiterIndex).trim();
      const rawValue = entry.slice(delimiterIndex + 1).trim();
      const nextFieldName = resolveNestedFieldName(fieldName, stripMatchingQuotes(rawKey));

      return extractMessagesFromStringPayload(rawValue, nextFieldName, options);
    });
  }

  if (normalizedValue.startsWith("[") && normalizedValue.endsWith("]")) {
    const arrayBody = normalizedValue.slice(1, -1).trim();

    if (!arrayBody) {
      return [];
    }

    return splitTopLevelValues(arrayBody).flatMap((item) =>
      extractMessagesFromStringPayload(item, fieldName, options)
    );
  }

  const cleanedMessage = applyTransform(
    parseErrorDetailString(stripMatchingQuotes(normalizedValue)),
    fieldName,
    options
  );

  return formatFieldMessage(cleanedMessage, fieldName, options);
};

export const extractMessagesFromPayload = (
  payload: unknown,
  fieldName?: string,
  options?: ExtractMessagesOptions
): string[] => {
  if (payload == null) {
    return [];
  }

  if (isErrorDetailPayload(payload)) {
    const detailMessage =
      typeof payload.string === "string"
        ? applyTransform(parseErrorDetailString(payload.string), fieldName, options)
        : "";

    return formatFieldMessage(detailMessage, fieldName, options);
  }

  if (typeof payload === "string") {
    return extractMessagesFromStringPayload(payload, fieldName, options);
  }

  if (Array.isArray(payload)) {
    return payload.flatMap((item) => extractMessagesFromPayload(item, fieldName, options));
  }

  if (typeof payload === "object") {
    return Object.entries(payload as Record<string, unknown>)
      .filter(([key]) => !NON_MESSAGE_KEYS.has(key))
      .flatMap(([key, value]) =>
        extractMessagesFromPayload(value, resolveNestedFieldName(fieldName, key), options)
      );
  }

  // A boolean or numeric leaf is never a displayable validation message, so it is
  // dropped in favour of the caller's fallback rather than shown as `Field: true`.
  return [];
};

// Structured validation errors a resolver attached as `extensions.validation_errors`,
// keyed by the server's field name. Read from the same places as the domain codes below.
export const extractFieldErrors = (error: unknown): Record<string, string[]> => {
  const candidate = error as any;
  const graphQLErrors: any[] = Array.isArray(candidate?.graphQLErrors)
    ? candidate.graphQLErrors
    : [];
  const networkErrors: any[] = Array.isArray(candidate?.networkError?.result?.errors)
    ? candidate.networkError.result.errors
    : [];
  const collected: Record<string, string[]> = {};

  for (const graphQLError of [...graphQLErrors, ...networkErrors]) {
    const validationErrors = graphQLError?.extensions?.validation_errors;

    if (
      !validationErrors ||
      typeof validationErrors !== "object" ||
      Array.isArray(validationErrors)
    ) {
      continue;
    }

    Object.entries(validationErrors as Record<string, unknown>).forEach(([field, messages]) => {
      const values = (Array.isArray(messages) ? messages : [messages]).filter(
        (message): message is string => typeof message === "string" && message.trim().length > 0
      );

      if (values.length) {
        collected[field] = [...(collected[field] ?? []), ...values];
      }
    });
  }

  return collected;
};

const readDomainCode = (value: unknown): ApiErrorDomain | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  return DOMAIN_CODES[value.toUpperCase()];
};

const collectDomainCodes = (error: any): ApiErrorDomain | undefined => {
  const graphQLErrors: any[] = Array.isArray(error?.graphQLErrors) ? error.graphQLErrors : [];
  const networkErrors: any[] = Array.isArray(error?.networkError?.result?.errors)
    ? error.networkError.result.errors
    : [];

  for (const graphQLError of [...graphQLErrors, ...networkErrors]) {
    const domain =
      readDomainCode(graphQLError?.extensions?.code) ??
      readDomainCode(graphQLError?.extensions?.domain);
    if (domain) return domain;
  }

  if (isAxiosError(error)) {
    const data: any = error.response?.data;
    return readDomainCode(data?.code) ?? readDomainCode(data?.error_code);
  }

  return undefined;
};

const resolveDomainFromMessages = (messages: string[]): ApiErrorDomain | undefined => {
  for (const [domain, pattern] of DOMAIN_MESSAGE_PATTERNS) {
    if (messages.some((message) => pattern.test(message))) {
      return domain;
    }
  }

  return undefined;
};

const applyDomainCopy = (messages: string[], domain?: ApiErrorDomain) => {
  if (domain !== "DECLINED_DEBTOR") {
    return messages;
  }

  // When the domain came from a backend code rather than the message text, the
  // messages carry no recognizable copy to swap, so use the product copy directly.
  if (!messages.some((message) => DECLINED_DEBTOR_PATTERN.test(message))) {
    return [DECLINED_DEBTOR_MESSAGE];
  }

  return messages.map((message) =>
    DECLINED_DEBTOR_PATTERN.test(message) ? DECLINED_DEBTOR_MESSAGE : message
  );
};

const collectMessages = (error: unknown, options?: ExtractMessagesOptions): string[] => {
  if (error == null) {
    return [];
  }

  if (typeof error === "string") {
    return extractMessagesFromStringPayload(error, undefined, options);
  }

  const candidate = error as any;
  const messages: string[] = [];

  if (Array.isArray(candidate?.graphQLErrors)) {
    candidate.graphQLErrors.forEach((graphQLError: any) => {
      messages.push(...extractMessagesFromPayload(graphQLError?.message, undefined, options));
    });
  }

  if (Array.isArray(candidate?.networkError?.result?.errors)) {
    candidate.networkError.result.errors.forEach((graphQLError: any) => {
      messages.push(...extractMessagesFromPayload(graphQLError?.message, undefined, options));
    });
  }

  if (messages.length) {
    return messages;
  }

  if (isAxiosError(candidate)) {
    const data = candidate.response?.data;

    if (data != null) {
      // A gateway or proxy failure returns an HTML document as the body, which
      // flattens into one enormous "message". Drop those so the fallback is used.
      const payloadMessages = extractMessagesFromPayload(data, undefined, options).filter(
        (message) => message.length <= MAX_DISPLAYABLE_MESSAGE_LENGTH
      );
      if (payloadMessages.length) {
        return payloadMessages;
      }
    }

    // `Request failed with status code 400` carries nothing actionable, so it is
    // dropped in favour of the caller's fallback.
    return [];
  }

  if (candidate instanceof Error) {
    return extractMessagesFromStringPayload(candidate.message, undefined, options);
  }

  if (Array.isArray(candidate) || typeof candidate === "object") {
    return extractMessagesFromPayload(candidate, undefined, options);
  }

  return extractMessagesFromStringPayload(String(candidate), undefined, options);
};

export function normalizeApiError(
  error: unknown,
  options?: NormalizeApiErrorOptions
): NormalizedApiError {
  const { fallback = DEFAULT_API_ERROR_MESSAGE, ...extractOptions } = options ?? {};
  const collected = collectMessages(error, extractOptions);
  const domain = collectDomainCodes(error) ?? resolveDomainFromMessages(collected);
  const messages = Array.from(new Set(applyDomainCopy(collected, domain).filter(Boolean)));

  return {
    message: messages.length ? messages.join("\n") : fallback,
    messages,
    domain,
    raw: error
  };
}

export function getApiErrorMessage(error: unknown, fallback?: string): string {
  return normalizeApiError(error, { fallback }).message;
}

// Used only to find a candidate URL inside the raw error text. The candidate is not trusted as
// a link on its own — it is parsed and validated below.
const TRANSACTION_URL_PATTERN = /https?:\/\/[^\s"'<>]+\/haul-pay\/[^\s"'<>]+\/transaction\/?/i;
// The exact funding request transaction path shape (id is a uuid). Only the path is trusted.
const TRANSACTION_PATH_PATTERN = /^\/haul-pay\/[0-9a-f-]+\/transaction\/?$/i;

const collectRawErrorStrings = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }
  const anyError = error as any;
  const parts: string[] = [];
  if (Array.isArray(anyError?.graphQLErrors)) {
    parts.push(...anyError.graphQLErrors.map((graphQLError: any) => graphQLError?.message ?? ""));
  }
  if (Array.isArray(anyError?.networkError?.result?.errors)) {
    parts.push(
      ...anyError.networkError.result.errors.map((graphQLError: any) => graphQLError?.message ?? "")
    );
  }
  if (typeof anyError?.message === "string") {
    parts.push(anyError.message);
  }
  const responseData = anyError?.response?.data;
  if (typeof responseData === "string") {
    parts.push(responseData);
  } else if (responseData) {
    try {
      parts.push(JSON.stringify(responseData));
    } catch {
      // ignore payloads that cannot be serialized
    }
  }
  return parts.join(" ");
};

// Pulls the funding request transaction path out of a duplicate-load-number error so it can be
// rendered as a real anchor. The origin from the error payload is discarded and only a validated
// transaction path is returned (relative to the current app), so the link can never resolve to an
// external origin. Returns undefined when no valid transaction path is present.
export const extractDuplicateLoadUrl = (error: unknown): string | undefined => {
  const haystack = collectRawErrorStrings(error);
  const hrefMatch = /href=["']([^"']+)["']/i.exec(haystack);
  const candidate = hrefMatch?.[1] ?? TRANSACTION_URL_PATTERN.exec(haystack)?.[0];
  if (!candidate) {
    return undefined;
  }
  try {
    const parsed = new URL(candidate);
    if (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      TRANSACTION_PATH_PATTERN.test(parsed.pathname)
    ) {
      return parsed.pathname;
    }
  } catch {
    // candidate was not a parseable absolute URL
  }
  return undefined;
};
